import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Spacer,
  FormErrorMessage,
} from "@chakra-ui/react";
import DataTable from "react-data-table-component";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import VaccineInventoryChart from "./VaccineInventoryChart";
import {
  Search,
  ExportExcel,
  ExportPdf,
  Print,
  Clear,
} from "../../themes/IconButtonSet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTable } from "@fortawesome/free-solid-svg-icons";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";


// ฟังก์ชันโหลดฟอนต์ Sarabun
const loadSarabunFont = async () => {
  try {
    const fontResponse = await fetch("/fonts/Sarabun-Regular.ttf");
    if (!fontResponse.ok) {
      throw new Error(
        `Failed to fetch Sarabun font: ${fontResponse.statusText}`
      );
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const VaccineInventoryRegionSummaryReport = () => {
  const { sessionId } = useAuth();
  const [filterData, setFilterData] = useState({
    VaccineID: "",
    WarehouseType: "",
    LotID: "",
    ProductionDate: "",
    ExpiryDate: "",
    ExpiryDays: "",
    LivestockTypeID: "",
  });
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [warehouseTypeOptions, setWarehouseTypeOptions] = useState([]);
  const [livestockTypeOptions, setLivestockTypeOptions] = useState([]);
  const [inventoryData, setInventoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // ดึงข้อมูล Vaccine List
  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
      } else {
        console.error("Failed to fetch vaccines:", response.error);
      }
    } catch (error) {
      console.error("Error fetching vaccines:", error);
    }
  };

  // ดึงข้อมูล Warehouse Type List
  const fetchWarehouseTypeOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehouseTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setWarehouseTypeOptions(response.TypeList || []);
        console.log("Warehouse Type Options:", response.TypeList); // Debug
      } else {
        console.error("Failed to fetch warehouse types:", response.error);
      }
    } catch (error) {
      console.error("Error fetching warehouse types:", error);
    }
  };

  // ดึงข้อมูล Livestock Type List
  const fetchLivestockTypeOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getLivestockTypeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const types = response.LivestockTypeList || [];
        setLivestockTypeOptions(types);
        if (types.length === 0) {
          console.warn("LivestockTypeList is empty");
        }
      } else {
        console.error("Failed to fetch livestock types:", response.error);
      }
    } catch (error) {
      console.error("Error fetching livestock types:", error);
    }
  };

  // เรียก API getVaccineInventoryByWarehouseLevel
  const fetchInventoryReport = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineInventoryByWarehouseLevel",
        data: {
          VaccineID: filterData.VaccineID || "",
          WarehouseType: filterData.WarehouseType || "",
          LotID: filterData.LotID || "",
          ProductionDate: filterData.ProductionDate || "",
          ExpiryDate: filterData.ExpiryDate || "",
          ExpiryDays: filterData.ExpiryDays || "",
          LivestockTypeID: filterData.LivestockTypeID || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setInventoryData(response.InventoryReport || []);
      } else {
        setInventoryData([]);
        alert("ไม่พบข้อมูล: " + response.error);
      }
    } catch (error) {
      console.error("Error fetching inventory:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchVaccineOptions();
      fetchWarehouseTypeOptions();
      fetchLivestockTypeOptions();
    }
  }, [sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData({ ...filterData, [name]: value });
  };

  // เตรียมข้อมูลสำหรับตารางหลัก (กรณี WarehouseType เป็น Region ให้จัดกลุ่ม)
  const prepareTableData = () => {
    if (filterData.WarehouseType === "Region") {
      // จัดกลุ่มตาม VaccineNameTH, VaccineNameEN, LivestockTypeID
      const groupedData = {};
      inventoryData.forEach((item) => {
        const key = `${item.VaccineNameTH}|${item.VaccineNameEN}|${item.LivestockTypeID}`;
        if (!groupedData[key]) {
          groupedData[key] = {
            VaccineNameTH: item.VaccineNameTH,
            VaccineNameEN: item.VaccineNameEN,
            WarehouseType: item.WarehouseType,
            LivestockTypeID: item.LivestockTypeID,
            TotalQuantity: 0,
            RegionID: item.RegionID,
            Provinces: [],
          };
        }
        groupedData[key].TotalQuantity += Number(item.TotalQuantity) || 0;
        groupedData[key].Provinces.push(item);
      });
      return Object.values(groupedData);
    }
    return inventoryData;
  };

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อวัคซีน (TH)</span>
        </HStack>
      ),
      selector: (row) => row.VaccineNameTH,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อวัคซีน (EN)</span>
        </HStack>
      ),
      selector: (row) => row.VaccineNameEN,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ประเภทคลัง</span>
        </HStack>
      ),
      selector: (row) => row.WarehouseType,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อคลัง</span>
        </HStack>
      ),
      selector: (row) => row.WarehouseName,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>Lot No.</span>
        </HStack>
      ),
      selector: (row) => row.LotID || "-",
      sortable: true,
      width: "200px",
      omit: filterData.WarehouseType === "Region",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>จำนวนคงคลัง</span>
        </HStack>
      ),
      selector: (row) => Number(row.TotalQuantity)?.toLocaleString() || 0,
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ประเภทปศุสัตว์</span>
        </HStack>
      ),
      selector: (row) => row.LivestockType || "-",
      sortable: true,
      width: "150px",
    },
  ];

  // คอลัมน์สำหรับตาราง Drill-Down (แสดงรายละเอียดระดับจังหวัด)
  const provinceColumns = [
    {
      name: "จังหวัด",
      selector: (row) => row.Province,
      sortable: true,
      width: "150px",
    },
    {
      name: "ชื่อวัคซีน (TH)",
      selector: (row) => row.VaccineNameTH,
      sortable: true,
    },
    {
      name: "ชื่อวัคซีน (EN)",
      selector: (row) => row.VaccineNameEN,
      sortable: true,
    },
    {
      name: "Lot No.",
      selector: (row) => row.LotID || "-",
      sortable: true,
      width: "200px",
    },
    {
      name: "วันที่ผลิต",
      selector: (row) =>
        row.ProductionDate ? new Date(row.ProductionDate).toLocaleDateString("th-TH") : "-",
      sortable: true,
      width: "200px",
    },
    {
      name: "วันที่หมดอายุ",
      selector: (row) =>
        row.ExpiryDate ? new Date(row.ExpiryDate).toLocaleDateString("th-TH") : "-",
      sortable: true,
      width: "200px",
    },
    {
      name: "อายุคงเหลือ (วัน)",
      selector: (row) => row.ExpiryDays || "-",
      sortable: true,
      width: "200px",
    },
    {
      name: "จำนวนคงคลัง",
      selector: (row) => Number(row.TotalQuantity)?.toLocaleString() || 0,
      sortable: true,
      width: "200px",
    },
    {
      name: "ประเภทปศุสัตว์",
      selector: (row) => row.LivestockTypeID || "-",
      sortable: true,
      width: "150px",
    },
  ];

  const exportToExcel = () => {
    const wsData = prepareTableData().map((item) => ({
      "ชื่อวัคซีน (TH)": item.VaccineNameTH,
      "ชื่อวัคซีน (EN)": item.VaccineNameEN,
      "ประเภทคลัง": item.WarehouseType,
      "Lot No.": item.LotID || "-",
      "วันที่ผลิต": item.ProductionDate
        ? new Date(item.ProductionDate).toLocaleDateString("th-TH")
        : "-",
      "วันที่หมดอายุ": item.ExpiryDate
        ? new Date(item.ExpiryDate).toLocaleDateString("th-TH")
        : "-",
      "อายุคงเหลือ (วัน)": item.ExpiryDays || "-",
      "จำนวนคงคลัง": Number(item.TotalQuantity)?.toLocaleString() || 0,
      "ประเภทปศุสัตว์": item.LivestockTypeID || "-",
    }));
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "InventoryRegionSummaryReport");
    XLSX.writeFile(wb, "VaccineInventoryRegionSummaryReport.xlsx");
  };

  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      doc.addFileToVFS("Sarabun.ttf", fontBase64);
      doc.addFont("Sarabun.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
    }

    doc.setFontSize(16);
    doc.text("รายงานสรุปวัคซีนคงคลังตามระดับคลัง (ประเภทคลัง)", 14, 10);
    doc.setFontSize(10);
    doc.text(`วันที่: ${new Date().toLocaleDateString("th-TH")}`, 14, 18);

    autoTable(doc, {
      startY: 25,
      head: [
        [
          "ชื่อวัคซีน (TH)",
          "ชื่อวัคซีน (EN)",
          "ประเภทคลัง",
          filterData.WarehouseType !== "Region" ? "Lot No." : "",
          filterData.WarehouseType !== "Region" ? "วันที่ผลิต" : "",
          filterData.WarehouseType !== "Region" ? "วันที่หมดอายุ" : "",
          filterData.WarehouseType !== "Region" ? "อายุคงเหลือ (วัน)" : "",
          "จำนวนคงคลัง",
          "ประเภทปศุสัตว์",
        ].filter(Boolean),
      ],
      body: prepareTableData().map((item) => [
        item.VaccineNameTH,
        item.VaccineNameEN,
        item.WarehouseType,
        filterData.WarehouseType !== "Region" ? (item.LotID || "-") : null,
        filterData.WarehouseType !== "Region" && item.ProductionDate
          ? new Date(item.ProductionDate).toLocaleDateString("th-TH")
          : null,
        filterData.WarehouseType !== "Region" && item.ExpiryDate
          ? new Date(item.ExpiryDate).toLocaleDateString("th-TH")
          : null,
        filterData.WarehouseType !== "Region" ? (item.ExpiryDays || "-") : null,
        Number(item.TotalQuantity)?.toLocaleString() || 0,
        item.LivestockTypeID || "-",
      ].filter((_, index) => !columns[index].omit)),
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 12,
        cellPadding: 2,
      },
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        fillColor: [22, 160, 133],
        textColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 50 },
        1: { cellWidth: 50 },
        2: { cellWidth: 30 },
        3: { cellWidth: 30 },
        4: { cellWidth: 30 },
        5: { cellWidth: 30 },
        6: { cellWidth: 30 },
        7: { cellWidth: 30 },
        8: { cellWidth: 30 },
      },
      didParseCell: (data) => {
        data.cell.styles.cellWidth = "auto";
      },
    });

    doc.save("VaccineInventoryRegionSummaryReport.pdf");
  };

  const printTable = () => {
    const printContent = `
      <html>
        <head>
          <title>Vaccine Inventory Region Summary Report</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h2>Vaccine Inventory Region Summary Report</h2>
          <table>
            <thead>
              <tr>
                <th>ชื่อวัคซีน (TH)</th>
                <th>ชื่อวัคซีน (EN)</th>
                <th>ประเภทคลัง</th>
                ${filterData.WarehouseType !== "Region" ? "<th>Lot No.</th>" : ""}
                ${filterData.WarehouseType !== "Region" ? "<th>วันที่ผลิต</th>" : ""}
                ${filterData.WarehouseType !== "Region" ? "<th>วันที่หมดอายุ</th>" : ""}
                ${filterData.WarehouseType !== "Region" ? "<th>อายุคงเหลือ (วัน)</th>" : ""}
                <th>จำนวนคงคลัง</th>
                <th>ประเภทปศุสัตว์</th>
              </tr>
            </thead>
            <tbody>
              ${prepareTableData()
                .map(
                  (item) => `
                    <tr>
                      <td>${item.VaccineNameTH}</td>
                      <td>${item.VaccineNameEN}</td>
                      <td>${item.WarehouseType}</td>
                      ${
                        filterData.WarehouseType !== "Region"
                          ? `<td>${item.LotID || "-"}</td>`
                          : ""
                      }
                      ${
                        filterData.WarehouseType !== "Region"
                          ? `<td>${
                              item.ProductionDate
                                ? new Date(item.ProductionDate).toLocaleDateString(
                                    "th-TH"
                                  )
                                : "-"
                            }</td>`
                          : ""
                      }
                      ${
                        filterData.WarehouseType !== "Region"
                          ? `<td>${
                              item.ExpiryDate
                                ? new Date(item.ExpiryDate).toLocaleDateString(
                                    "th-TH"
                                  )
                                : "-"
                            }</td>`
                          : ""
                      }
                      ${
                        filterData.WarehouseType !== "Region"
                          ? `<td>${item.ExpiryDays || "-"}</td>`
                          : ""
                      }
                      <td>${Number(item.TotalQuantity)?.toLocaleString() || 0}</td>
                      <td>${item.LivestockTypeID || "-"}</td>
                    </tr>
                  `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  // ฟังก์ชันสำหรับแสดงตาราง Drill-Down
  const expandableRow = (row) => {
    if (filterData.WarehouseType !== "Region" || !row.Provinces) {
      return null;
    }
    return (
      <Box p={4} bg="gray.50">
        <DataTable
          columns={provinceColumns}
          data={row.Provinces}
          noHeader
          highlightOnHover
          striped
          responsive
          customStyles={tableCustomStyles}
        />
      </Box>
    );
  };

  return (
    <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <Heading as="h2" mb={10} textAlign="center">
        รายงานสรุปวัคซีนคงคลังตามระดับคลัง (ประเภทคลัง)
      </Heading>

      {/* ฟอร์มฟิลเตอร์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faSearch} color="white" size="xl" />
            <Heading size="lg" fontWeight="normal">
              ค้นหาข้อมูลคงคลังตามระดับคลัง (ประเภทคลัง)
            </Heading>
          </HStack>
        </Box>
        <VStack spacing={6} p={5}>
          <HStack spacing={6} w="100%" flexWrap="wrap">
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ประเภทคลัง
              </FormLabel>
              <Select
                name="WarehouseType"
                value={filterData.WarehouseType}
                onChange={handleInputChange}
                placeholder="เลือกประเภทคลัง (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {warehouseTypeOptions.map((type) => (
                  <option key={type.TypeID} value={type.TypeID}>
                    {type.TypeName}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ประเภทปศุสัตว์
              </FormLabel>
              <Select
                name="LivestockTypeID"
                value={filterData.LivestockTypeID}
                onChange={handleInputChange}
                placeholder="เลือกประเภทปศุสัตว์ (ทั้งหมด)"
                colorScheme="iBlue"
              >
              {livestockTypeOptions.map((type) => (
                <option
                  key={type.LivestockTypeID}
                  value={type.LivestockTypeID}
                >
                  {type.LivestockTypeName}
                </option>
              ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วัคซีน
              </FormLabel>
              <Select
                name="VaccineID"
                value={filterData.VaccineID}
                onChange={handleInputChange}
                placeholder="เลือกวัคซีน (ทั้งหมด)"
                colorScheme="iBlue"
              >
                {vaccineOptions.map((vaccine) => (
                  <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
                    {vaccine.VaccineNameTH}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                Lot No.
              </FormLabel>
              <Input
                name="LotID"
                value={filterData.LotID}
                onChange={handleInputChange}
                placeholder="กรอก Lot No. (ถ้ามี)"
                colorScheme="iBlue"
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                อายุคงเหลือ น้อยกว่า (วัน)
              </FormLabel>
              <Input
                name="ExpiryDays"
                value={filterData.ExpiryDays}
                onChange={handleInputChange}
                placeholder="กรอกจำนวนวัน (ถ้ามี)"
                colorScheme="iBlue"
              />
            </FormControl>
          </HStack>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() =>
                  setFilterData({
                    VaccineID: "",
                    WarehouseType: "",
                    LotID: "",
                    ProductionDate: "",
                    ExpiryDate: "",
                    ExpiryDays: "",
                    LivestockTypeID: "",
                  })
                }
              />
              <Search onClick={fetchInventoryReport} isLoading={isLoading} />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* ตาราง */}
      {inventoryData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <Box
            mb={5}
            borderRadius="full"
            w="100%"
            bgColor="iOrange.500"
            color="white"
            p={2}
          >
            <HStack spacing={5} ml={5}>
              <FontAwesomeIcon icon={faTable} color="white" size="xl" />
              <Heading size={{ base: "md", md: "lg" }} fontWeight="normal">
                รายการสรุปวัคซีนคงคลังตามระดับคลัง (ประเภทคลัง)
              </Heading>
            </HStack>
          </Box>
          <HStack spacing={5} m={5}>
            <Spacer />
            <ExportExcel onClick={exportToExcel} />
            <ExportPdf onClick={exportToPDF} />
            <Print onClick={printTable} />
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={prepareTableData()}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
              expandableRows={filterData.WarehouseType === "Region"}
              expandableRowsComponent={expandableRow}
            />
          </Box>
        </Box>
      )}

      {/* กราฟ */}
      {inventoryData.length > 0 && (
        <VaccineInventoryChart
          data={inventoryData}
          warehouseOptions={warehouseTypeOptions.map((type) => ({
            WarehouseID: type.TypeID,
            WarehouseName: type.TypeName,
          }))}
        />
      )}
    </Box>
  );
};

export default VaccineInventoryRegionSummaryReport;